/**
 * Sequence my day (3.2): on request, propose an order for a day's visits.
 *
 * The day's shape is not up for negotiation — the slots stay where they are and
 * so does everything fixed in them: blocks, visits already delivered, and any
 * window the patient has confirmed. What moves is which patient sits in which
 * slot, chosen nearest-neighbour from the clinician's starting point. The engine
 * proposes; the drive cost of both orders comes from the product, and the
 * clinician accepts or does not.
 *
 * **Except where two slots collide.** Pete, 2026-09-21: *"if I drag a visit from
 * one day to another and it messes up the schedule it would be nice to be able
 * to use the optimize button to fix it"*. A dragged visit lands at a time of its
 * own and can sit on top of one that was already there; dealing patients into
 * the day's existing start times preserved that overlap exactly, because the
 * overlap **was** one of the slots. So after dealing, the day is swept in time
 * order and a movable stop that would start inside the one before it is pushed
 * to where that one ends. Nothing moves that was not colliding, which is why
 * this is not the engine taking ownership of the day: two visits at once is not
 * a shape anybody chose.
 *
 * What it will not do: move a fixed stop. A confirmed window dropped on top of a
 * block is a conflict between two things the clinician said were immovable, and
 * the engine reports it rather than picking one.
 */

export interface SequenceStop {
  /** Stable key for the drive-time engine (`"<visitResourceId>"` or `"calendarBlock-<id>"`). */
  key: string;
  kind: "visit" | "block";
  /** Visit resource id, or calendar block id. */
  id: number;
  visitId?: number;
  label: string;
  startIso: string;
  endIso: string;
  /** "lat,lng" as the product stores it. Absent when the stop has no address. */
  coordinates?: string;
  /** Fixed stops keep their slot: blocks, delivered visits, confirmed windows. */
  fixed: boolean;
  reason?: string;
}

export interface SequenceChange {
  key: string;
  id: number;
  visitId: number;
  label: string;
  fromIso: string;
  toIso: string;
  toEndIso: string;
}

export interface SequenceProposal {
  current: SequenceStop[];
  proposed: SequenceStop[];
  changes: SequenceChange[];
  /** Overlaps left standing because both stops are fixed. The engine will not choose between them. */
  fixedConflicts: { a: string; b: string }[];
  /** Set when the proposal's last stop ends after the shift does. Reported, never refused. */
  pastShiftEnd?: { label: string; endIso: string; shiftEndIso: string };
}

export interface SequenceOptions {
  /** The shift's end, so a day that no longer fits inside it can say so. */
  shiftEndIso?: string;
}

export function parseCoordinates(value: string | undefined | null): [number, number] | undefined {
  if (!value) return undefined;
  const [lat, lng] = value.split(",").map((x) => Number(x.trim()));
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return undefined;
  return [lat, lng];
}

const EARTH_KM = 6371;

/** Great-circle kilometres. Only ever used to rank one candidate against another. */
export function distanceKm(a: string | undefined, b: string | undefined): number {
  const from = parseCoordinates(a);
  const to = parseCoordinates(b);
  if (!from || !to) return Number.POSITIVE_INFINITY;
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLat = toRad(to[0] - from[0]);
  const dLng = toRad(to[1] - from[1]);
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(from[0])) * Math.cos(toRad(to[0])) * Math.sin(dLng / 2) ** 2;
  return 2 * EARTH_KM * Math.asin(Math.min(1, Math.sqrt(h)));
}

function durationMs(stop: SequenceStop): number {
  return new Date(stop.endIso).getTime() - new Date(stop.startIso).getTime();
}

const ms = (iso: string) => new Date(iso).getTime();
const isFixed = (s: SequenceStop) => s.fixed || s.kind === "block";

/** Deal the movable visits into the day's own slots, nearest first from where the clinician starts. */
function dealNearestFirst(current: SequenceStop[], originCoordinates?: string): SequenceStop[] {
  const remaining = current.filter((s) => !isFixed(s));
  const dealt: SequenceStop[] = [];
  let previous = originCoordinates;
  for (const slot of current) {
    if (isFixed(slot)) {
      dealt.push(slot);
      previous = slot.coordinates ?? previous;
      continue;
    }
    let bestIndex = 0;
    let best = Number.POSITIVE_INFINITY;
    remaining.forEach((candidate, i) => {
      const d = distanceKm(previous, candidate.coordinates);
      // A stop with no address never displaces one that has one; ties keep the day's order.
      const score = Number.isFinite(d) ? d : Number.MAX_SAFE_INTEGER;
      if (score < best) {
        best = score;
        bestIndex = i;
      }
    });
    const [chosen] = remaining.splice(bestIndex, 1);
    const startIso = slot.startIso;
    const endIso = new Date(ms(startIso) + durationMs(chosen)).toISOString();
    dealt.push({ ...chosen, startIso, endIso });
    previous = chosen.coordinates ?? previous;
  }
  return dealt;
}

/**
 * Sweep a dealt day in time order and push any movable stop that would start
 * inside the one before it, or inside a fixed stop, to where that one ends. A
 * stop that is not colliding is not touched, so the sweep is invisible on a day
 * that was already clean. Fixed stops never move; two of them on top of each
 * other come back as a conflict for somebody else to settle.
 */
function deOverlap(dealt: SequenceStop[]): { laid: SequenceStop[]; fixedConflicts: { a: string; b: string }[] } {
  const fixed = dealt.filter(isFixed).sort((a, b) => ms(a.startIso) - ms(b.startIso));
  const fixedConflicts: { a: string; b: string }[] = [];
  for (let i = 0; i < fixed.length; i += 1) {
    for (let j = i + 1; j < fixed.length; j += 1) {
      if (ms(fixed[i].endIso) > ms(fixed[j].startIso)) fixedConflicts.push({ a: fixed[i].label, b: fixed[j].label });
    }
  }
  const laid: SequenceStop[] = [];
  let cursor = Number.NEGATIVE_INFINITY;
  for (const stop of dealt) {
    if (isFixed(stop)) {
      laid.push(stop);
      cursor = Math.max(cursor, ms(stop.endIso));
      continue;
    }
    const duration = durationMs(stop);
    let start = Math.max(ms(stop.startIso), cursor);
    // `fixed` is ascending and `start` only ever moves right, so one pass settles it.
    for (const f of fixed) if (start < ms(f.endIso) && start + duration > ms(f.startIso)) start = ms(f.endIso);
    laid.push({ ...stop, startIso: new Date(start).toISOString(), endIso: new Date(start + duration).toISOString() });
    cursor = start + duration;
  }
  // A movable stop pushed past a fixed one can leapfrog it, so the day is re-read in time order.
  laid.sort((a, b) => ms(a.startIso) - ms(b.startIso));
  return { laid, fixedConflicts };
}

/**
 * Propose an order. Slots are the stops' own start times in time order; fixed
 * stops keep theirs, and the movable visits are dealt into the rest by proximity
 * to whatever came before, starting from the clinician's own address. The dealt
 * day is then swept so that nothing starts inside anything else.
 */
export function proposeSequence(stops: SequenceStop[], originCoordinates?: string, options: SequenceOptions = {}): SequenceProposal {
  const current = [...stops].sort((a, b) => a.startIso.localeCompare(b.startIso));
  const movable = current.filter((s) => !isFixed(s));
  // One movable visit is nothing to re-order — but it can still be sitting on a
  // block, or on the confirmed window the drag dropped it beside, and that is
  // exactly the case Pete hit. So the sweep runs whether or not the deal does.
  const dealt = movable.length < 2 ? current : dealNearestFirst(current, originCoordinates);
  const { laid: proposed, fixedConflicts } = deOverlap(dealt);

  const changes: SequenceChange[] = [];
  for (const stop of proposed) {
    if (stop.kind !== "visit" || stop.visitId === undefined) continue;
    const before = current.find((s) => s.key === stop.key);
    if (!before || before.startIso === stop.startIso) continue;
    changes.push({ key: stop.key, id: stop.id, visitId: stop.visitId, label: stop.label, fromIso: before.startIso, toIso: stop.startIso, toEndIso: stop.endIso });
  }

  const last = proposed[proposed.length - 1];
  const pastShiftEnd =
    options.shiftEndIso && last && ms(last.endIso) > ms(options.shiftEndIso)
      ? { label: last.label, endIso: last.endIso, shiftEndIso: options.shiftEndIso }
      : undefined;
  return { current, proposed, changes, fixedConflicts, pastShiftEnd };
}

export interface DriveTimed {
  eventKey?: string | null;
  startOfShiftDriveTime?: string | number | null;
  interVisitDriveTime?: string | number | null;
  endOfShiftDriveTime?: string | number | null;
}

/**
 * **`simpleDriveTime` answers with a diff, not with a day.**
 * `calculateShiftDriveTimes` keeps an event only when its drive times came back
 * different from the ones it was handed (`changed ? … : null`, then a filter),
 * so pricing a day that is already correct returns the **empty array** and
 * pricing a re-ordered one returns **only the stops that moved**.
 *
 * Totalling the reply on its own therefore priced Dana's Wednesday at *0 minutes
 * now, 13 proposed* while the day's own footer read 40 (Pete, 2026-09-21: *"the
 * drive total for optimize is off in this case"*). Neither number was the day.
 *
 * So the reply is laid over the events it was asked about, and a stop the engine
 * did not mention keeps the minutes it already had — which is exactly what the
 * engine means by leaving it out.
 */
export function applyDriveUpdates<T extends DriveTimed>(events: T[], updates: DriveTimed[]): DriveTimed[] {
  const byKey = new Map(updates.filter((u) => u.eventKey).map((u) => [String(u.eventKey), u]));
  return events.map((e) => (e.eventKey && byKey.get(String(e.eventKey))) || e);
}

/** Total drive of an order, as the product's engine returned it, in minutes. */
export function driveTotalMinutes(segments: { startOfShiftDriveTime?: string | number | null; interVisitDriveTime?: string | number | null; endOfShiftDriveTime?: string | number | null }[]): number {
  const num = (v: string | number | null | undefined) => (typeof v === "number" ? v : v ? parseInt(v, 10) || 0 : 0);
  return segments.reduce((total, s) => total + num(s.startOfShiftDriveTime) + num(s.interVisitDriveTime) + num(s.endOfShiftDriveTime), 0);
}
